/**
 * 416 (#1431): the zone editor's edits, kept pure so every rule is tested without a map. Corners are [lng, lat] (the
 * stored order). Each edit records the corners it replaced, so Undo walks back one edit at a time, Clear included.
 * Whether a shape is valid (crossing edges, distance) is the platform's to decide; the editor only knows that fewer
 * than three corners cannot be a zone.
 */
export type Corner = [number, number];
export interface ZoneState {
  corners: Corner[];
  history: Corner[][];
}

const edit = (s: ZoneState, corners: Corner[]): ZoneState => ({
  corners,
  history: [...s.history, s.corners],
});

export const addCorner = (s: ZoneState, c: Corner) => edit(s, [...s.corners, c]);
export const moveCorner = (s: ZoneState, i: number, c: Corner) =>
  edit(
    s,
    s.corners.map((x, j) => (j === i ? c : x)),
  );
export const removeCorner = (s: ZoneState, i: number) =>
  edit(
    s,
    s.corners.filter((_, j) => j !== i),
  );
export const clearZone = (s: ZoneState) => edit(s, []);
export function undo(s: ZoneState): ZoneState {
  if (s.history.length === 0) return s;
  return { corners: s.history[s.history.length - 1] as Corner[], history: s.history.slice(0, -1) };
}

/** What Save would send: the corners (three or more), null to clear, or nothing savable (one or two corners). */
export function zoneOf(s: ZoneState): { savable: boolean; polygon: Corner[] | null } {
  if (s.corners.length === 0) return { savable: true, polygon: null };
  if (s.corners.length < 3) return { savable: false, polygon: null };
  return { savable: true, polygon: s.corners };
}
